export enum ListAccountType {
  SA = 'SA',
  DSP = 'DSP',
  MA = 'MA',
}

export interface EligibilityStatus {
  isEligible: boolean;
  reasonCode?: string;
}

export interface LinkedManagerAccount {
  managerAccountId: string;
  managerAccountName: string;
}

export interface ListedAccount {
  accountId: string;
  accountName: string;
  accountType: ListAccountType;
  accountIdDisplay: string;
  linkedManagerAccount?: LinkedManagerAccount;
  eligibilityStatus: EligibilityStatus;
  isExpandable?: boolean;
  childCount?: number;
}

export interface ChooseAccountsContextResponse {
  accounts: ListedAccount[];
  paginationToken?: string;
  totalCount?: number;
  query?: string;
}

export interface ManagerAccountChildrenResponse {
  managerAccountId: string;
  managerAccountName: string;
  children: ListedAccount[];
  paginationToken?: string;
  totalCount?: number;
}

export interface SaBusinessDetails {
  businessName: string;
  phoneNumber: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  billingRegistrationNumber: string;
  displayName: string;
  currency: string;
  timezone: string;
  industry: string;
}

export interface DspAccountDetails {
  industry: string;
  timezone: string;
  currency: string;
  businessName: string;
  phoneNumber: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
}

export interface AccountDetailsResponse {
  saBusinessDetails?: SaBusinessDetails;
  dspAccountDetails?: DspAccountDetails;
}

export interface ReviewDspAccount {
  name: string;
  cfid: string;
  managerAccountId?: string | null;
  managerAccountName?: string | null;
}

export interface ReviewSaAccount {
  name: string;
  globalAccountId: string;
}

export interface ReviewNewAccount {
  name: string;
  globalAccountId: string;
  entityId?: string;
}

export interface ReviewAccountsResponse {
  dspAccount: ReviewDspAccount;
  sponsoredAdsAccount: ReviewSaAccount;
  newAccount: ReviewNewAccount;
}

export enum CombinationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface CombineAccountsResponse {
  operationId: string;
  status: CombinationStatus;
}

/**
 * What became of one account in an upgrade request.
 *
 * `accepted` means GRS took the request for this account, not that the account has been upgraded:
 * the combination runs asynchronously afterwards, so nothing here reports completion.
 *
 * `invitationId` exists on the shared result model but is never populated for an upgrade. An
 * invitation exists only to obtain consent from a counterparty, and an upgrade has none, so GRS
 * records no invitation on this path.
 */
export interface CombinationAccountResult {
  dspAccountId: string;
  accepted: boolean;
  invitationId?: string;
  failureReason?: string;
}

/**
 * The two statuses ARMS can report for an account.
 *
 * There is deliberately no failure value, because ARMS defines none: a combination that fails after
 * being accepted is indistinguishable from one still running, so a caller must treat "not CREATED"
 * as unknown rather than as failed, and must stop asking eventually.
 */
export const ACCOUNT_STATUS_CREATED = 'CREATED';
export const ACCOUNT_STATUS_PARTIALLY_CREATED = 'PARTIALLY_CREATED';

/**
 * What the upgrade status endpoint reports about one requested DSP account.
 *
 * Keyed on the DSP account id rather than the global account id, because an upgrade creates a new
 * global account whose id the caller never learns. ARMS resolves that internally.
 */
export interface UpgradeStatusEntry {
  dspAccountId: string;
  /**
   * Whether ARMS could resolve this DSP account to the global account the upgrade created.
   *
   * False is an ordinary early answer rather than an error: the resolution depends on links the
   * asynchronous registration is still making. Treat it exactly like PARTIALLY_CREATED.
   */
  resolved: boolean;
  /** Present once resolved. Not an entity id, so it cannot scope the campaigns deep link. */
  globalAccountId?: string;
  /** GAMS's value passed through verbatim. Only CREATED and PARTIALLY_CREATED occur today. */
  accountStatus?: string;
  /**
   * Set when this account's status could not be read, for example when the caller does not own it.
   *
   * Per entry rather than for the whole request, so one unreadable account cannot blank the batch.
   * Independent of `resolved`: an entry can be resolved and still carry this, which means the
   * account exists and the upgrade is progressing but its status could not be read. Treat it as
   * still running and retry, never as a failure.
   */
  error?: string;
  /**
   * The identifier campaign manager scopes on. Present only once the entry is resolved and CREATED,
   * since the entity does not exist until the asynchronous registration creates it.
   */
  regionalEntityId?: string;
  /**
   * The account's own URL, whose origin is the host its marketplace is reachable on. Present under
   * the same conditions as `regionalEntityId`. Only the origin is used.
   */
  url?: string;
}

/**
 * Status for a batch of accounts, one entry per requested id.
 *
 * Entries must be matched back on `dspAccountId`, never on position: the endpoint is not obliged to
 * answer in request order, and a batch can come back partially.
 */
export interface UpgradeStatusResponse {
  results: UpgradeStatusEntry[];
}

/**
 * Response to an upgrade request, carrying one result per requested account.
 *
 * There is deliberately no overall status: a partial outcome is a success, so the per-account
 * results are the whole report. The request fails outright only when GRS accepted none of them.
 */
export interface UpgradeAccountsResponse {
  accountResults: CombinationAccountResult[];
}

export type InvoiceRecipientType = 'ADVERTISER_ACCOUNT' | 'MANAGER_ACCOUNT';

export interface BillingSummary {
  invoiceRecipient: InvoiceRecipientType;
  /**
   * Pre-formatted bill-to billing addresses from the account's unique bill-to
   * billing profiles. Matches the backend BillingDetailsResponse contract.
   */
  billingAddresses: string[] | null;
  /**
   * Seller Central origin for a Sponsored ads account whose Amazon business is a seller.
   * A seller maintains billing details in Seller Central rather than in a billing profile, so
   * when this is present the tab points there instead of rendering `billingAddresses`, whether
   * or not that holds anything. Only the origin is meaningful; no path is appended.
   *
   * Null for every other account, and always null on the Amazon DSP tab.
   */
  sellerCentralUrl: string | null;
  /**
   * Payment methods per country from the account's payment agreements.
   * Null when payment data is unavailable for this account.
   */
  paymentMethods: PaymentMethodInfo[] | null;
}

/** One payment agreement entry: the country it covers and its primary payment method type. */
export interface PaymentMethodInfo {
  countryCode: string;
  agreementType: string;
  paymentMethodType: string;
}

export interface BillingDetailsResponse {
  sponsoredAdsBilling: BillingSummary | null;
  amazonDspBilling: BillingSummary | null;
}

export interface EditedAccountDetails {
  displayName?: string;
  businessName?: string;
  phoneNumber?: string;
  website?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
  billingRegistrationNumber?: string;
  industry?: string;
  currency?: string;
  timeZone?: string;
}

/**
 * The response a caller can apply to a pending combination invitation.
 *
 * Sent as the `status` of the update request, and named for the action rather than the resulting
 * state: GRS resolves `REJECTED` by caller identity, revoking the invitation when the original
 * requester withdraws their own request and declining it when any other admin refuses one.
 *
 * `RESENT` is modelled by ARMS but **not yet accepted by GRS**, whose update activity admits only
 * APPROVED and REJECTED and answers anything else with an invalid-input error. The "Send again"
 * action therefore fails until GRS adds it; it is wired to the intended contract rather than to
 * today's behaviour, since the alternative is shipping the design without the control.
 */
export enum CombinationInvitationUpdateStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  RESENT = 'RESENT',
}

/** Which side of an invitation the viewer is on, and so which actions it offers. */
export enum CombinationInvitationDirection {
  /** Awaiting the viewer's answer: they can approve or decline it. */
  RECEIVED = 'RECEIVED',
  /** Raised by the viewer: they can send it again or cancel it. */
  SENT = 'SENT',
  /**
   * The viewer administers both sides. Grouped with Received so they can approve or decline it;
   * rare in practice, since an admin on both sides normally combines directly without a request.
   */
  SENT_AND_RECEIVED = 'SENT_AND_RECEIVED',
}

/**
 * One account-combination invitation, as the Requests table needs it.
 *
 * The first five fields plus `direction` are returned by the BFF. The account names and dates after
 * them are required by the design but **not yet returned by the BFF**, so each is optional and
 * renders as a dash when absent. They are typed here rather than omitted so the table matches the
 * approved design and the remaining work is plumbing rather than more UI.
 */
export interface CombinationInvitationSummary {
  /** The invitation's id, and what the update endpoint takes. */
  accountCombinationInvitationId: string;
  /**
   * The Sponsored Ads account the invitation would combine into, in ACI format.
   *
   * Absent on an invitation with no target, which is how GRS represents an upgrade. No such
   * invitation is ever recorded today, so in practice every row here is a combination.
   */
  advertiserAccountId?: string;
  /**
   * The accounts being combined in, flattened out of the GRS wrapper. One entry in practice, the
   * Amazon DSP account's CFID, because GRS refuses an invitation naming more than one.
   */
  accountIdsToCombine: string[];
  /**
   * The invitation's state, exactly as GRS reported it.
   *
   * Deliberately a string rather than an enum, matching the BFF: GRS can report a state neither
   * service models, and coercing it would hide the real value from a reader. Render an
   * unrecognized value as-is rather than dropping the row.
   */
  status: string;
  /** GRS's detail about the state, including rejection reasons. Often absent. */
  statusMessage?: string;

  /**
   * Which side of the invitation the viewer is on, used to split the rows into the Received and
   * Sent tabs. Passed through from GRS by the BFF; absent when GRS does not populate it, in which
   * case the row is shown under Received.
   */
  direction?: CombinationInvitationDirection;
  /** Display name of the Amazon DSP account. **Not supplied by the BFF.** */
  dspAccountName?: string;
  /** Display name of the Sponsored Ads account. **Not supplied by the BFF.** */
  sponsoredAdsAccountName?: string;
  /** ISO 8601 date the request was raised. **Not supplied by the BFF.** */
  requestedOn?: string;
  /** ISO 8601 date the request lapses. **Not supplied by the BFF.** */
  expiresOn?: string;
  /**
   * The combined-into account's entity, used to scope the campaign manager link offered on an
   * approved request.
   *
   * Supplied only once the combination has actually finished: the invitation must be approved
   * **and** the account it combined into must have reached `CREATED`. Approving alone is not
   * enough, because it only starts the combination.
   */
  regionalEntityId?: string;
  /**
   * An absolute URL on the combined-into account's marketplace domain.
   *
   * **Only the origin is used.** The path is whatever the service's URL builder produced for the
   * account type, which is a billing page rather than anything to do with campaigns, and is never
   * followed: the campaign manager path is composed here from this origin plus
   * `regionalEntityId`. Neither side should start depending on the path.
   *
   * Present under the same conditions as `regionalEntityId`, so an approved request whose account
   * is still being built shows a dash rather than a link into a half-made account.
   */
  url?: string;
}

/** A page of the invitations visible to the caller, covering both directions. */
export interface QueryCombinationInvitationsResponse {
  /** Empty rather than absent when the caller has none. */
  accountCombinationInvitations: CombinationInvitationSummary[];
  /** Opaque, and passed straight back to fetch the next page. Absent on the last page. */
  nextToken?: string;
}

/**
 * What GRS reports the invitation to be after an update.
 *
 * `status` is what GRS observed rather than what was requested, and stays absent when GRS
 * confirmed the update without describing the invitation, so it cannot be relied on to echo the
 * action that was taken.
 */
export interface UpdateCombinationInvitationResponse {
  accountCombinationInvitationId: string;
  status?: string;
  statusMessage?: string;
}

// Aliases used by page components
export type CombineAccountType = ListAccountType;
export const CombineAccountType = ListAccountType;
export type EligibleAccount = ListedAccount;
